<template>
  <div class="camera-modal" data-test-id="CameraComponent">
    <img v-if="pictureUrl" :src="pictureUrl" class="camera-stream" />

    <video v-else ref="video" class="camera-stream" muted />

    <div class="camera-modal-container">
      <section v-if="!picture" class="camera-modal-actions">
        <b-iconstack v-if="canSwitchCamera" font-scale="3" variant="white" @click="switchCamera">
          <b-icon icon="camera" stacked variant="danger"></b-icon>
          <b-icon icon="arrow-right-short" scale="0.5" shift-v="-9" shift-h="3" stacked variant="danger"></b-icon>
          <b-icon icon="arrow-left-short" scale="0.5" shift-v="-9" shift-h="-3" stacked variant="danger"></b-icon>
        </b-iconstack>
        <b-button variant="danger" class="take-picture-button" @click="capture">
          <i>Take Picture</i>
        </b-button>
      </section>

      <section v-else>
        <b-button variant="danger" class="take-picture-button" @click="retake">
          <i>Retake</i>
        </b-button>
        <b-button variant="danger" class="take-picture-button" @click="usePhoto">
          <i>Use Photo</i>
        </b-button>
      </section>
    </div>
  </div>
</template>

<script>
import { createMediaDevicesIterator } from "@/Case/sharedUserMediaFunctions/createMediaDevicesIterator";
import { stopAllTracks } from "@/Case/sharedUserMediaFunctions/stopAllTracks";
import { switchCamera } from "@/Case/sharedUserMediaFunctions/switchCamera";
import { dataURItoBlob } from "@/Utils/blobUtils";
import { CaseEvent } from "@/Case/CaseEvent";

export default {
  name: "Camera",

  props: {
    eventType: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      canSwitchCamera: true,
      currentDeviceID: "",
      mediaStream: null,
      picture: null,
      pictureUrl: null,
    };
  },

  async mounted() {
    this.setupMedia();

    if ((await this.createMediaDevicesIterator()) < 2) {
      this.canSwitchCamera = false;
    }

    await this.switchCamera();
  },

  destroyed() {
    this.stopAllTracks();
  },

  methods: {
    createMediaDevicesIterator,

    stopAllTracks,

    switchCamera,

    setupMedia() {
      if (navigator.mediaDevices === undefined) {
        navigator.mediaDevices = {};
      }
      if (navigator.mediaDevices.getUserMedia === undefined) {
        navigator.mediaDevices.getUserMedia = this.legacyGetUserMediaSupport();
      }
    },

    legacyGetUserMediaSupport() {
      return (constraints) => {
        // First get ahold of the legacy getUserMedia, if present
        let getUserMedia =
          navigator.getUserMedia ||
          navigator.webkitGetUserMedia ||
          navigator.mozGetUserMedia ||
          navigator.msGetUserMedia ||
          navigator.oGetUserMedia;
        // Some browsers just don't implement it - return a rejected promise with an error
        // to keep a consistent interface
        if (!getUserMedia) {
          return Promise.reject(new Error("getUserMedia is not implemented in this browser"));
        }
        // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
        return new Promise((resolve, reject) => {
          getUserMedia.call(navigator, constraints, resolve, reject);
        });
      };
    },

    async capture() {
      if (typeof ImageCapture !== "undefined" && this.mediaStream) {
        const mediaStreamTrack = this.mediaStream.getVideoTracks()[0];
        const imageCapture = new ImageCapture(mediaStreamTrack);

        const blob = await imageCapture.takePhoto();

        this.picture = blob;
        this.pictureUrl = URL.createObjectURL(blob);
      } else {
        const canvas = document.createElement("canvas");
        canvas.width = this.$refs.video.offsetWidth;
        canvas.height = this.$refs.video.offsetHeight;

        const context = canvas.getContext("2d");
        context.drawImage(this.$refs.video, 0, 0, canvas.width, canvas.height);

        this.pictureUrl = canvas.toDataURL("image/png");
        this.picture = dataURItoBlob(this.pictureUrl);
      }
    },

    async retake() {
      let constraints = {
        video: { deviceId: this.currentDeviceID },
      };

      this.picture = null;
      this.pictureUrl = null;

      this.stopAllTracks();

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      this.mediaStream = mediaStream;
      this.$refs.video.srcObject = mediaStream;
      this.$refs.video.play();
    },

    usePhoto() {
      this.$emit(CaseEvent.MODIFY, {
        eventType: this.eventType,
        data: this.picture,
        id: `${this.eventType}.jpeg`,
      });
    },
  },
};
</script>

<style scoped>
.camera-modal {
  height: 100%;
  width: 100%;
  z-index: 10;
}

.camera-stream {
  max-height: 100%;
  width: 100%;
}

.camera-modal-container {
  margin-bottom: 24px;
  width: 100%;
}

.camera-modal-actions {
  align-items: center;
  display: flex;
  justify-content: center;
}

.camera-modal-container button {
  margin: 20px;
}

.take-picture-button i {
  margin: auto;
  width: 90px;
}
</style>
